import { DataWithPagination, createApiCall } from 'services/api';
import { TransferOrderStatus, TransferOrderType } from './types';
import { PaginationDirection } from 'services/url';

import {
  DisplayTransferOrder,
  TransferOrder,
  CreateTransferOrderRequest,
  GenerateTransferOrderNumberRequest,
  GenerateTransferOrderNumberResponse,
} from './types';

// Add more mock transfer orders for testing
const MOCK_TRANSFER_ORDERS: DisplayTransferOrder[] = [
  {
    id: 1,
    number: 'MTO 1000',
    status: 'Draft' as any,
    type: 'MTO' as any,
    sourceName: 'Main Warehouse',
    destinationName: 'Store A',
    lastUpdated: '2024-08-20T14:30:00.000Z',
    totalQuantity: 10,
    totalItems: 3,
    deleted: false,
  },
  {
    id: 2,
    number: 'STO 1001',
    status: 'In Transit' as any,
    type: 'STO' as any,
    sourceName: 'Main Warehouse',
    destinationName: 'Store B',
    lastUpdated: '2024-08-19T16:45:00.000Z',
    totalQuantity: 25,
    totalItems: 5,
    deleted: false,
  },
];

// Fetch all transfer orders (like fetchItemsAPI)
export const fetchTransferOrdersAPI = async (): Promise<
  DataWithPagination<DisplayTransferOrder>
> => {
  // For now, return mock data
  // Later this will be: await createApiCall({ path: '/v1/transfer-orders' })
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: MOCK_TRANSFER_ORDERS,
        pagination: {
          page: 1,
          pageSize: 25,
          totalRows: MOCK_TRANSFER_ORDERS.length,
          sort: { sortBy: 'number', direction: PaginationDirection.Ascending },
        },
      });
    }, 500); // Simulate API delay
  });
};

// Fetch single transfer order (like fetchItem)
export const fetchTransferOrder = async (
  id: number
): Promise<TransferOrder | null> => {
  // Later this will be: await createApiCall({ path: `/v1/transfer-orders/${id}` })
  return new Promise((resolve) => {
    setTimeout(() => {
      const found = MOCK_TRANSFER_ORDERS.find((order) => order.id === id);
      if (!found) return resolve(null);
      const transferOrder: TransferOrder = {
        id: found.id,
        number: found.number,
        status: found.status,
        type: found.type,
        sourceLocationId: null,
        destinationLocationId: null,
        sourceLocation: { id: 0, name: found.sourceName },
        destinationLocation: { id: 0, name: found.destinationName },
        transferReason: null,
        dateCreated: null,
        dateLastModified: null,
        dateIssued: null,
        dateFulfilled: null,
        deleted: found.deleted,
        totalQuantity: found.totalQuantity,
        totalItems: found.totalItems,
        createdByUserId: null,
        lastModifiedByUserId: null,
        items: [],
        attachments: [],
        notes: [],
        auditLog: [],
      };
      resolve(transferOrder);
    }, 300);
  });
};

// Create new transfer order (like postItem)
export const postTransferOrder = async (
  transferOrder: CreateTransferOrderRequest
): Promise<TransferOrder> => {
  // Later this will be: await createApiCall({ path: '/v1/transfer-orders', method: 'POST', body: transferOrder })
  return new Promise((resolve) => {
    setTimeout(() => {
      const newOrder: TransferOrder = {
        id: Math.max(...MOCK_TRANSFER_ORDERS.map((o) => o.id || 0)) + 1,
        number: `MTO ${Date.now()}`,
        status: TransferOrderStatus.Draft,
        type: TransferOrderType.MTO,
        sourceLocationId: transferOrder.sourceLocationId,
        destinationLocationId: transferOrder.destinationLocationId,
        sourceLocation: {
          id: transferOrder.sourceLocationId || 1,
          name: 'Mock Source',
        },
        destinationLocation: {
          id: transferOrder.destinationLocationId || 2,
          name: 'Mock Destination',
        },
        transferReason: transferOrder.transferReason || null,
        dateCreated: new Date(),
        dateLastModified: new Date(),
        dateIssued: null,
        dateFulfilled: null,
        deleted: false,
        totalQuantity: 0,
        totalItems: 0,
        createdByUserId: 1,
        lastModifiedByUserId: 1,
        items: [],
        attachments: [],
        notes: [],
        auditLog: [],
      };
      resolve(newOrder);
    }, 500);
  });
};

// Delete transfer order (like deleteItem)
export const deleteTransferOrder = async (
  transferOrder: TransferOrder
): Promise<void> => {
  // Later this will be: await createApiCall({ path: `/v1/transfer-orders/${transferOrder.id}`, method: 'DELETE' })
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log(`Deleted transfer order: ${transferOrder.number}`);
      resolve();
    }, 300);
  });
};

// Get next transfer order number (like SO and PO flows)
export const getTransferOrderId = async (): Promise<string> => {
  const response = await createApiCall({
    path: '/v1/transfer-orders/next-transfer-order-number',
    method: 'GET',
  })();

  return response.data;
};

// Generate transfer order number based on location selection
export const generateTransferOrderNumber = async (
  request: GenerateTransferOrderNumberRequest
): Promise<GenerateTransferOrderNumberResponse> => {
  // Use the new API endpoint
  const numericOrderNumber = await getTransferOrderId();

  // Determine type based on locations
  // For now, default to STO since we don't have location hierarchy in the mock
  // In real implementation, backend will determine this based on location root IDs
  const transferType =
    request.sourceLocationId === request.destinationLocationId
      ? TransferOrderType.MTO
      : TransferOrderType.STO;

  return {
    number: `${transferType} ${numericOrderNumber}`,
    type: transferType,
  };
};
